import {
  InboxEntry,
  InboxEntrySortField,
  InboxEntrySortOrder,
} from '@this+that/models';
import { compareEntriesManual } from '@this+that/utils';

export const sortInboxEntries = (entries: InboxEntry[]): InboxEntry[] =>
  entries.filter((entry): entry is InboxEntry => entry != null);

const A_AFTER_B = 1;
const A_BEFORE_B = -1;
const EQUAL = 0;

const compareIds = (
  a: InboxEntry,
  b: InboxEntry,
  ascending: boolean,
): number => {
  if (a.id === b.id) {
    return EQUAL;
  }
  const result = a.id < b.id ? A_BEFORE_B : A_AFTER_B;
  return ascending ? result : -result;
};

const compareValues = (a: number, b: number, ascending: boolean): number => {
  const diff = a - b;
  return ascending ? diff : -diff;
};

type FieldGetter = (entry: InboxEntry) => number | null | undefined;

const partitionByFieldPresence = (
  entries: InboxEntry[],
  getFieldValue: FieldGetter,
): { withField: InboxEntry[]; withoutField: InboxEntry[] } => {
  const withField: InboxEntry[] = [];
  const withoutField: InboxEntry[] = [];
  entries.forEach((entry) => {
    if (getFieldValue(entry) == null) {
      withoutField.push(entry);
    } else {
      withField.push(entry);
    }
  });
  return { withField, withoutField };
};

const sortWithField = (
  entries: InboxEntry[],
  getFieldValue: FieldGetter,
  ascending: boolean,
): InboxEntry[] =>
  [...entries].sort((a, b) => {
    const aValue = getFieldValue(a) as number;
    const bValue = getFieldValue(b) as number;
    return aValue === bValue
      ? compareIds(a, b, ascending)
      : compareValues(aValue, bValue, ascending);
  });

const sortByFieldWithFallback = (
  entries: InboxEntry[],
  getFieldValue: FieldGetter,
  scanIndexForward: boolean,
): InboxEntry[] => {
  const { withField, withoutField } = partitionByFieldPresence(
    entries,
    getFieldValue,
  );
  // withoutField falls back to updatedAt using the same scanIndexForward as
  // the primary field, so it inherits priority's already-inverted direction.
  return [
    ...sortWithField(withField, getFieldValue, scanIndexForward),
    ...sortWithField(
      withoutField,
      (entry) => entry.updatedAt,
      scanIndexForward,
    ),
  ];
};

const sortByDateField = (
  entries: InboxEntry[],
  getFieldValue: FieldGetter,
  isAsc: boolean,
): InboxEntry[] => sortByFieldWithFallback(entries, getFieldValue, isAsc);

const sortByPriority = (
  entries: InboxEntry[],
  isAsc: boolean,
): InboxEntry[] => {
  // URGENT = 0 numerically, so ascending numeric order surfaces urgent first.
  const scanIndexForward = !isAsc;
  return sortByFieldWithFallback(
    entries,
    (entry) => entry.priority,
    scanIndexForward,
  );
};

export const sortInboxEntriesByConfig = (
  entries: InboxEntry[],
  sortField: InboxEntrySortField,
  sortOrder: InboxEntrySortOrder,
): InboxEntry[] => {
  const filtered = entries.filter(
    (entry): entry is InboxEntry => entry != null,
  );
  const isAsc = sortOrder === InboxEntrySortOrder.ASC;

  if (sortField === InboxEntrySortField.CREATED_AT) {
    return sortByDateField(filtered, (entry) => entry.createdAt, isAsc);
  }
  if (sortField === InboxEntrySortField.UPDATED_AT) {
    return sortByDateField(filtered, (entry) => entry.updatedAt, isAsc);
  }
  if (sortField === InboxEntrySortField.DUE_DATE) {
    return sortByDateField(filtered, (entry) => entry.dueAt, isAsc);
  }
  if (sortField === InboxEntrySortField.PRIORITY) {
    return sortByPriority(filtered, isAsc);
  }
  if (sortField === InboxEntrySortField.MANUAL) {
    return [...filtered].sort(compareEntriesManual);
  }
  return filtered;
};
